"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SectionTitle } from "@/components/SectionTitle";
import {
  Users,
  Shield,
  Zap
} from "lucide-react";
import { useRouter } from "next/navigation";
import { getFeaturedProducts } from "@/static-stores/productStore";
import { getAllStalls } from "@/static-stores/stallStore";
import { type IProduct } from "@/lib/types/productType";
import { type Stall } from "@/lib/api/stallApi";
import { ProductGrid } from "@/components/product/ProductGrid";
import { StallGrid } from "@/components/stalls/StallGrid";
import { HeroCarousel } from "@/components/HeroCarousel";

const featuredCategories = [
  {
    id: "el",
    name: "Electronics",
    description: "Smartphones, laptops, gaming",
    icon: "📱",
    productCount: 1250
  },
  {
    id: "cl",
    name: "Fashion",
    description: "Clothing, accessories, shoes",
    icon: "👕",
    productCount: 890
  },
  {
    id: "hg",
    name: "Home & Garden",
    description: "Furniture, decor, appliances",
    icon: "🏠",
    productCount: 650
  },
  {
    id: "co",
    name: "Collectibles",
    description: "Art, antiques, memorabilia",
    icon: "🎨",
    productCount: 320
  }
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState<IProduct[]>([]);
  const [featuredStalls, setFeaturedStalls] = useState<Stall[]>([]);
  const router = useRouter();

  useEffect(() => {
    // Load featured products and stalls
    const allStalls = getAllStalls();
    const featuredProductsData = getFeaturedProducts();

    // Get featured products (first 8)
    setFeaturedProducts(featuredProductsData.slice(0, 8));

    // Get featured stalls (first 6)
    setFeaturedStalls(allStalls.slice(0, 6));
  }, []);

  const handleCarouselButtonClick = (buttonType: 'primary' | 'secondary', messageId: string) => {
    // Handle carousel button clicks
    console.log(`Carousel button clicked: ${buttonType} for ${messageId}`);

    // You can add navigation logic here
    switch (messageId) {
      case 'buyers':
        if (buttonType === 'primary') {
          // Navigate to shopping page
          window.location.href = '/search';
        } else {
          // Navigate to categories
          window.location.href = '/categories';
        }
        break;
      case 'sellers-marketplace':
        if (buttonType === 'primary') {
          // Navigate to seller registration
          window.location.href = '/register';
        } else {
          // Navigate to seller dashboard
          window.location.href = '/seller-dashboard';
        }
        break;
      case 'sellers-whitelabel':
        if (buttonType === 'primary') {
          // Navigate to white label info
          window.location.href = '/enterprise';
        } else {
          // Navigate to learn more
          window.location.href = '/about';
        }
        break;
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    // Navigate to search page with the selected category
    router.push(`/search?category=${categoryId}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Carousel Section */}
      <HeroCarousel onButtonClick={handleCarouselButtonClick} />

      {/* Stats Section */}
      <section className="py-16 px-6 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">10K+</div>
              <div className="text-gray-600 dark:text-gray-300">Active Sellers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">50K+</div>
              <div className="text-gray-600 dark:text-gray-300">Products Listed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">100K+</div>
              <div className="text-gray-600 dark:text-gray-300">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">4.9★</div>
              <div className="text-gray-600 dark:text-gray-300">Average Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <ProductGrid title="Featured products" variant="gray" products={featuredProducts} />


      {/* Featured Categories */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <SectionTitle title="Featured Categories" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
            {featuredCategories.map((category) => (
              <Card
                key={category.id}
                className="hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handleCategoryClick(category.id)}
              >
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                    {category.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{category.name}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    {category.description}
                  </p>
                  <Badge variant="secondary" className="text-xs">
                    {category.productCount} items
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Items Grid */}
      <ProductGrid title="Trending Now" variant="gray" products={featuredProducts} />
      
      {/* Featured Sellers */}
      <StallGrid title="Featured Sellers" variant="white" stalls={featuredStalls} />
      
      {/* White-Label Landing Pages CTA */}
      <section className="py-16 px-6 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Want Your Own Branded Landing Page?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Give your stall its own professional landing page with your branding and showcase your best products.
              Perfect for sellers who want to stand out!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" asChild>
                <Link href="/subscription">
                  View Pricing Plans
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/seller/1">
                  See Demo Landing Page
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 px-6 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose Retrostall?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Join thousands of satisfied buyers and sellers who trust our platform
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardContent className="p-8">
                <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="font-semibold text-xl mb-3">Secure Transactions</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Bank-level security with buyer protection and secure payments
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-8">
                <Zap className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-xl mb-3">Fast & Reliable</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Quick delivery, real-time updates, and 24/7 customer support
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-8">
                <Users className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="font-semibold text-xl mb-3">Trusted Community</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Verified sellers, authentic reviews, and quality guarantee
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
